import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "../auth/AuthContext.js";
import { getAuthMode } from "../auth/config.js";
import { bindTenantIdGetter } from "../auth/http.js";
import { buildSessionFromTokens } from "../auth/session-from-tokens.js";
import { buildDemoSession, type AppSession, type DemoRole } from "./demo-session.js";

type SessionContextValue = {
  session: AppSession | null;
  /** Só em VITE_AUTH_MODE=demo */
  setRole: ((role: DemoRole) => void) | null;
  setCompanyId: (companyId: string) => void;
  isDemoMode: boolean;
};

const SessionContext = createContext<SessionContextValue | null>(null);

function useBindTenantHeader(companyId: string | undefined) {
  useEffect(() => {
    bindTenantIdGetter(() => companyId);
    return () => bindTenantIdGetter(null);
  }, [companyId]);
}

function DemoSessionProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<DemoRole>("EMPLOYEE");
  const [companyId, setCompanyId] = useState("co-matriz");
  useBindTenantHeader(companyId);

  const value = useMemo<SessionContextValue>(() => {
    const base = buildDemoSession(role);
    return {
      session: { ...base, companyId },
      setRole: (next) => {
        setRole(next);
        setCompanyId(buildDemoSession(next).companies[0]!.id);
      },
      setCompanyId,
      isDemoMode: true,
    };
  }, [role, companyId]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

function ApiSessionProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);

  const session = useMemo(() => {
    if (status !== "authenticated") return null;
    return buildSessionFromTokens(companyId);
  }, [companyId, status]);

  useBindTenantHeader(session?.companyId);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      setRole: null,
      setCompanyId: (id) => setCompanyId(id),
      isDemoMode: false,
    }),
    [session],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  if (getAuthMode() === "demo") {
    return <DemoSessionProvider>{children}</DemoSessionProvider>;
  }
  return <ApiSessionProvider>{children}</ApiSessionProvider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession fora de SessionProvider");
  return ctx;
}
